import { getCountryByCode, getCountryByName } from "../data/locationTaxonomy";

const DIAL_CODES = {
  DO: "+1",
  FR: "+33",
  DE: "+49",
  IT: "+39",
  ES: "+34",
  CH: "+41",
  US: "+1",
};

function resolveCountry(countryRef) {
  if (!countryRef) return null;
  if (typeof countryRef === "string" && countryRef.length === 2) {
    return getCountryByCode(countryRef);
  }
  return getCountryByName(countryRef) ?? getCountryByCode(countryRef);
}

export function countryCodeToFlag(countryCode) {
  if (!countryCode || countryCode.length !== 2) return "🌐";
  return countryCode
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));
}

export function getDialCodeForCountry(countryRef) {
  const country = typeof countryRef === "string" && countryRef.length === 2
    ? getCountryByCode(countryRef)
    : resolveCountry(countryRef);
  return DIAL_CODES[country?.code] ?? "+1";
}

export function normalizeDigits(value = "") {
  return String(value).replace(/\D/g, "");
}

/** Digits only — for wa.me links (e.g. 41791234567). */
export function phoneToWhatsAppDigits(phone = "") {
  return normalizeDigits(phone);
}

export function buildWhatsAppUrl(phone, message = "") {
  const digits = phoneToWhatsAppDigits(phone);
  if (!digits) return null;
  const base = `https://wa.me/${digits}`;
  if (!message) return base;
  return `${base}?text=${encodeURIComponent(message)}`;
}

export function buildTelUrl(phone) {
  const digits = phoneToWhatsAppDigits(phone);
  if (!digits) return null;
  return `tel:+${digits}`;
}

export function openExternalUrl(url) {
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
}

export function openTelLink(phone) {
  const url = buildTelUrl(phone);
  if (!url) return;
  window.location.href = url;
}

export function formatFullPhone(dialCode, nationalNumber) {
  const digits = normalizeDigits(nationalNumber);
  if (!digits) return "";
  const dial = dialCode?.startsWith("+") ? dialCode : `+${normalizeDigits(dialCode)}`;
  return `${dial}${digits}`;
}

export const MIN_PHONE_NATIONAL_DIGITS = 9;

export function isValidNationalPhone(nationalNumber, minDigits = MIN_PHONE_NATIONAL_DIGITS) {
  const digits = normalizeDigits(nationalNumber);
  return digits.length >= minDigits && digits.length <= 15;
}

export function isValidFullPhone(dialCode, nationalNumber) {
  return Boolean(dialCode) && isValidNationalPhone(nationalNumber);
}

export function splitFullPhone(fullPhone, fallbackDialCode = "+41") {
  if (!fullPhone) {
    return { dialCode: fallbackDialCode, national: "" };
  }

  const digits = normalizeDigits(fullPhone);
  if (!digits) {
    return { dialCode: fallbackDialCode, national: "" };
  }

  const dialCodes = [...DIAL_CODE_OPTIONS].sort(
    (a, b) => normalizeDigits(b).length - normalizeDigits(a).length,
  );

  for (const dial of dialCodes) {
    const dialDigits = normalizeDigits(dial);
    if (digits.startsWith(dialDigits)) {
      return {
        dialCode: dial,
        national: digits.slice(dialDigits.length),
      };
    }
  }

  return { dialCode: fallbackDialCode, national: digits };
}

export const DIAL_CODE_OPTIONS = [...new Set(Object.values(DIAL_CODES))].sort(
  (a, b) => Number(normalizeDigits(a)) - Number(normalizeDigits(b)),
);
